// Copyright (c) 2025, WSO2 LLC. (https://www.wso2.com). All Rights Reserved.
//
// This software is the property of WSO2 LLC. and its suppliers, if any.
// Dissemination of any information or reproduction of any material contained
// herein in any form is strictly forbidden, unless permitted by WSO2 expressly.
// You may not alter or remove any copyright or other notice from copies of this content.
import React, { useEffect, useState } from "react";
import { makeStyles } from "tss-react/mui";
import {
  Typography,
  TextField,
  Radio,
  RadioGroup,
  FormControlLabel,
  FormControl,
  Grid,
  Button,
  CircularProgress,
  Backdrop,
  Snackbar,
  LinearProgress
} from "@mui/material";
import { useHistory } from "react-router-dom";
import { AppConfig, FILE_UPLOAD_LIMIT, FINANCE_CHOOSE_FILE_SUBQUESTION_ID } from "@configs/admin-config";
import { getAccessToken } from "@utils/oauth";
import MuiAlert from "@mui/material/Alert";
import useHttp from "@utils/http";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import CloseIcon from "@mui/icons-material/Close";
import CloudUploadIcon from "@mui/icons-material/CloudUpload";

const useStyles = makeStyles()((theme) => ({
  backdrop: {
    zIndex: 2,
    color: "white",
  },
  questionParent: {
    display: "flex",
  },
  questionIndex: {
    flexBasis: "4%",
    "@media(max-width: 900px)": {
      flexBasis: "5%",
    },
    "@media(max-width: 600px)": {
      flexBasis: "10%",
    },
    "@media(max-width: 400px)": {
      flexBasis: "15%",
    },
  },
  questionDetails: {
    flexBasis: "96%",
    textAlign: "justify",
    "@media(max-width: 900px)": {
      flexBasis: "94%",
    },
    "@media(max-width: 600px)": {
      flexBasis: "90%",
    },
    "@media(max-width: 400px)": {
      flexBasis: "85%",
    },
  },
  verticalcenter: {
    display: "flex",
    flexDirection: "column",
    justifycontent: "center",
  },
  emailMsg: {
    color: "grey",
  },
  fileAttachment: {
    display: "flex",
    justifycontent: "center",
    flexWrap: "wrap",
    "& > *": {
      margin: theme.spacing(0.5),
    },
  },
  saveButton: {
    backgroundColor: theme.palette.secondary.main,
    color: "#ffffff",
    borderRadius: 4,
    padding: "4px 14px",
    textTransform: "none",
    fontWeight: 500,
    "&:hover": {
      backgroundColor: "#ba5502",
    },
  },
  fileBlock: {
    display: "flex",
    alignItems: "center",
    backgroundColor: "#f5f5f5",
    border: "1px solid #e0e0e0",
    borderRadius: 4,
    padding: "8px 12px",
    marginBottom: 8,
    cursor: "pointer",
    transition: "all 0.2s ease",
    width: "95%",
    "&:hover": {
      backgroundColor: "#ebebeb",
    },
  },
  fileIcon: {
    marginRight: 8,
    fontSize: 20,
    color: "#666",
  },
  fileName: {
    fontFamily: theme.typography.fontFamily,
    fontSize: "14px",
    color: "#333",
    marginRight: 4,
  },
  attachedFilesSection: {
    marginTop: 8,
    marginBottom: 8,
  },
  attachedFilesLabel: {
    fontFamily: theme.typography.fontFamily,
    fontSize: "14px",
    color: "#666",
    marginBottom: 4,
    display: "block",
  },
  cancelBtn: {
  backgroundColor: "transparent",
  color: "#000000",
  border: "1px solid #e0e0e0",
  borderRadius: 4,
  padding: "4px 14px",
  textTransform: "none",
  marginLeft: 8,
  fontWeight: 500,
  "&:hover": {
    backgroundColor: "#f5f5f5",
  },
},
fileCloseIcon: {
    marginLeft: 8,
    fontSize: 16,
    color: "#999",
    cursor: "pointer",
    transition: "color 0.2s ease",
    "&:hover": {
      color: "#666",
    },
  },
  dragDropZone: {
    border: "2px dashed #d0d0d0",
    borderRadius: 4,
    padding: "30px 20px",
    textAlign: "center",
    cursor: "pointer",
    transition: "all 0.3s ease",
    backgroundColor: "#fafafa",
    marginBottom: 16,
    "&:hover": {
      borderColor: "#999",
      backgroundColor: "#f5f5f5",
    },
  },
  dragDropZoneActive: {
    borderColor: theme.palette.primary.main,
    backgroundColor: "#e3f2fd",
  },
  dragDropZoneDisabled: {
    opacity: 0.5,
    cursor: "not-allowed",
    backgroundColor: "#f0f0f0",
    pointerEvents: "none",
    "&:hover": {
      borderColor: "#d0d0d0",
      backgroundColor: "#f0f0f0",
    },
  },
  uploadIcon: {
    fontSize: 48,
    color: "#999",
    marginBottom: 8,
  },
  uploadText: {
    fontFamily: theme.typography.fontFamily,
    fontSize: "15px",
    color: "#666",
    marginBottom: 4,
  },
  uploadTextLink: {
    color: theme.palette.secondary.main,
    cursor: "pointer",
  },
  uploadSubText: {
    fontFamily: theme.typography.fontFamily,
    fontSize: "13px",
    color: "#999",
  },
  fileInput: {
    display: "none",
  },
}));

const Alert = React.forwardRef(function Alert(props, ref) {
  return <MuiAlert elevation={6} variant="filled" ref={ref} {...props} />;
});

const SubQuestionDetails = (props) => {
  const { classes } = useStyles();
  const history = useHistory();
  const question = props.question;
  const subQuestion = props.subQuestion;
  const category = props.category;
  const { data, error, sendRequest, reqExtra} = useHttp();
  const [boolAnswer, setBoolAnswer] = useState(false);
  const [uploadedFiles, setFiles] = useState([]);
  const [uploadedNewFile, setUploadedNewFile] = useState([]);
  const [fileUploadSnack, setFileUploadSnack] = useState(false);
  const [fileUploadProgress, setFileUploadProgress] = useState(false);
  const [snackbarData, setSnackbarData] = useState({
      open: false,
      message: "",
      severity: "",
      onClose: null,
    });
  const [backdropOpen, setBackdropOpen] = useState(false);
  const [isDragging, setIsDragging] = useState(false);

  const getTotalCommentsForQuestions = () => {
    if (!props.comments) return 0;
    
    return props.comments.filter(comment => 
      comment.questionId === 42 || comment.questionId === 43
    ).length;
  };

  const shouldDisableRadioButtons = () => {
    const totalComments = getTotalCommentsForQuestions();
    const approvalEmailNotSent = !props.approvalEmailSent;
    return totalComments === 0 || approvalEmailNotSent;
  };

  useEffect(() => {
    props.disableCall(props.fieldDisabled, props.answer);
    // Converting Mysql 0/1 into Boolean True/False
    if (props.answer && props.answer[0] && props.answer[0].booleanAnswer) {
      setBoolAnswer(true);
    } else if (props.answer && props.answer[0] && !props.answer[0].booleanAnswer) {
      setBoolAnswer(false);
    }

    if (props.files && props.files.length > 0) {
      props.files.map((file) => {
        if (file.subQuestionId === subQuestion.subQuestionId) {
          setFiles((prev) => [...prev, file]);
        }
        return null;
      });
    }
  }, [subQuestion.subQuestionId, subQuestion.answerType]);

  useEffect(() => {
    if (data && reqExtra === AppConfig.fileUpload && !error) {
      setFileUploadProgress(false);
      if (data?.data?.fileName) {
        setUploadedNewFile((prev) => [...prev, { fileName: data.data.fileName }]);
      }
    } else if (error && reqExtra === AppConfig.fileUpload) {
      setFileUploadProgress(false);
    }
  }, [data, error, reqExtra]);

  useEffect(() => {
    if (data && reqExtra === AppConfig.deleteFile && !error) {
      setFileUploadProgress(false);
    } else if (error && reqExtra === AppConfig.deleteFile) {
      setFileUploadProgress(false);
    }
  }, [data, error, reqExtra]);

  // Handle Radio Button Inputs
  const handleRadioChange = (subQuestionId) => (event) => {
    const value = event.target.value;
    // If yes is selected
    if (value === "yes") {
      setBoolAnswer(true);
      props.parentCallback(subQuestionId, true, "", "", true);
    }
    // If no is selected
    else if (value === "no") {
      setBoolAnswer(false);
      props.parentCallback(subQuestionId, false, "", "", true);
    }
  };

  // Handle Text Inputs
  const handleFormChange = (subQuestionId) => (event) => {
    const newValue = event.target.value;
    const currentValue = props.answer && props.answer[0] ? props.answer[0].descriptionAnswer : "";

    if (newValue !== currentValue) {
      props.parentCallback(subQuestionId, "", newValue, "", true);
    }
  };

  const buildFileViewUrl = (baseUrl, fileName, extensionWithoutDot) => {
    return `${AppConfig.urls.ADMIN}${baseUrl}?fileName=${encodeURIComponent(
      fileName
    )}&extension=${encodeURIComponent(extensionWithoutDot)}`;
  };

  const onCloseSnackbar = () => {
    setSnackbarData({ ...snackbarData, open: false });
  };

  const saveFile = async (files, subQuestionId) => {
    const fileArray = Object.values(files);
    setFileUploadProgress(true);
    for (const file of fileArray) {
      let fileName = file.name;
      var extension = fileName.split('.').pop();
      var extensionWithoutDot = extension.toLowerCase();
      const requestURL =
          AppConfig.baseUrl +
          "partners/" +
          props.email + "/" +
          AppConfig.fileUpload +
          "?fileExtension=" +
          extensionWithoutDot +
          "&fileName=" +
          fileName;
      try {
        await sendRequest(requestURL, "POST", file, AppConfig.fileUpload);
      } catch (error) {
        setFileUploadProgress(false);
        setSnackbarData({
          message: "Upload failed: " + error.message,
          open: true,
          severity: "error",
          onClose: onCloseSnackbar,
        });
      }
    }
  };

  const handleFileUpload = (subQuestionId) => (event) => {
    const totalFiles = uploadedFiles.length + uploadedNewFile.length + event.target.files.length;
    
    if (totalFiles > FILE_UPLOAD_LIMIT) {
      setSnackbarData({
        message: `Cannot upload more than ${FILE_UPLOAD_LIMIT} files`,
        open: true,
        severity: "warning",
        onClose: onCloseSnackbar,
      });
      setFileUploadSnack(true);
    } else {
      saveFile(event.target.files, subQuestionId);
    }
  };

  const handleDragEnter = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const totalFiles = uploadedFiles.length + uploadedNewFile.length;
    if (totalFiles >= FILE_UPLOAD_LIMIT) {
      return;
    }
    setIsDragging(true);
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.stopPropagation();
  };

  const handleDrop = (e, subQuestionId) => {
    e.preventDefault();
    e.stopPropagation();
    setIsDragging(false);

    const files = e.dataTransfer.files;
    if (files && files.length > 0) {
      const totalFiles = uploadedFiles.length + uploadedNewFile.length + files.length;
      
      if (totalFiles > FILE_UPLOAD_LIMIT) {
        setSnackbarData({
          message: `Cannot upload more than ${FILE_UPLOAD_LIMIT} files`,
          open: true,
          severity: "warning",
          onClose: onCloseSnackbar,
        });
        setFileUploadSnack(true);
      } else {
        saveFile(files, subQuestionId);
      }
    }
  };

  const handleZoneClick = (subQuestionId) => {
    // Check if already at limit
    const totalFiles = uploadedFiles.length + uploadedNewFile.length;
    
    if (totalFiles >= FILE_UPLOAD_LIMIT) {
      setSnackbarData({
        message: `Maximum of ${FILE_UPLOAD_LIMIT} files already reached`,
        open: true,
        severity: "warning",
        onClose: onCloseSnackbar,
      });
      return; // Don't trigger file input
    }
    
    document.getElementById(`file-upload-${subQuestionId}`).click();
  };

  const deleteFile = async (fileName) => {
    if (fileName) {
      const requestURL =
        AppConfig.baseUrl +
        "partners/" +
        props.email +
        "/" +
        AppConfig.fileDelete +
        "/" +
        fileName;
      sendRequest(requestURL, "DELETE", null, AppConfig.deleteFile);
    }
  };

  const handleFileRemove = async (file) => {
    setFileUploadProgress(true);
    let fileName = file.fileName;
    setUploadedNewFile(uploadedNewFile.filter((item) => item.fileName !== fileName));
    deleteFile(fileName);
  };

  const handleClose = (event, reason) => {
    if (reason === "clickaway") {
      return;
    }
    setFileUploadSnack(false);
    setSnackbarData({ ...snackbarData, open: false });
  };

  const handleChipOnClick = (fileName) => {
    var extension = fileName.split('.').pop();
    var extensionWithoutDot = extension.toLowerCase();

    const PDF_URL = buildFileViewUrl(AppConfig.urls.VIEW_PDF, fileName, extensionWithoutDot);
    const IMAGE_URL = buildFileViewUrl(AppConfig.urls.VIEW_IMAGE, fileName, extensionWithoutDot);

    if (extension === "pdf") {
      window.open(PDF_URL, "_blank");
    } else if (extension === "jpg") {
      window.open(IMAGE_URL, "_blank");
    } else if (extension === "jpeg") {
      window.open(IMAGE_URL, "_blank");
    } else if (extension === "png") {
      window.open(IMAGE_URL, "_blank");
    } else {
      const url = `${AppConfig.FileServiceURL}${fileName}?fileExtension=${extensionWithoutDot}`;
      window.open(url, "_blank");
    }
  };

  const saveNewFiles = async () => {
  setBackdropOpen(true);
  
  const requestURL =
    AppConfig.baseUrl +
    "partners/" +
    props.email + "/" +
    AppConfig.saveNewFiles;
  
  const filesData = uploadedNewFile.map((file) => ({
    companyId: parseInt(props.companyId),
    questionId: question.questionId,
    subQuestionId: subQuestion.subQuestionId,
    fileName: file.fileName,
    fileType: file.fileName.split('.').pop().toLowerCase(),
  }));

  try {
    let response = await fetch(requestURL, {
      method: "POST",
      headers: {
        Accept: "application/json",
        "Content-Type": "application/json",
        Authorization: "Bearer " + getAccessToken(),
      },
      body: JSON.stringify(filesData), 
    });
    
    let body = await response.json();
    
    if (response.status === 201) {
      const newFiles = uploadedNewFile.map(file => ({ fileName: file.fileName }));
      setFiles((prev) => [...prev, ...newFiles]);
      
      uploadedNewFile.forEach(file => {
        props.parentCallback(subQuestion.subQuestionId, "", "", file.fileName, true);
      });
      
      setSnackbarData({
        message: "All files saved successfully",
        open: true,
        severity: "success",
        onClose: onCloseSnackbar,
      });
      
      setUploadedNewFile([]);
    } else {
      setSnackbarData({
        message: "Save failed: " + body.message,
        open: true,
        severity: "error",
        onClose: onCloseSnackbar,
      });
    }
  } catch (error) {
    setSnackbarData({
      message: "Upload failed: " + error.message,
      open: true,
      severity: "error",
      onClose: onCloseSnackbar,
    });
  } finally {
    setBackdropOpen(false);
  }
};

const cancelNewFiles = () => {
  setUploadedNewFile([]);
};

const formatDisplayFileName = (fileName) => {
  if (!fileName) return "file";
  
  if (fileName.length > 25) {
    return fileName.substring(24, 50);
  }
  
  return fileName;
};

  // Check if file limit is reached
  const isFileLimitReached = uploadedFiles.length + uploadedNewFile.length >= FILE_UPLOAD_LIMIT;

  return (
    <React.Fragment>
      {/* Render Boolean Input */}
      <Backdrop open={backdropOpen} className={classes.backdrop}>
        <CircularProgress color="secondary" />
      </Backdrop>
      <Snackbar anchorOrigin={{ vertical: "bottom", horizontal: "center" }} open={snackbarData.open} onClose={handleClose} autoHideDuration={6000}>
        <Alert onClose={handleClose} severity={snackbarData.severity}>
          {snackbarData.message}
        </Alert>
      </Snackbar>
      {question.questionId === subQuestion.questionId && subQuestion.answerType && (
        <Grid item xs={12}>
          <div className={classes.questionParent}>
            <div className={classes.questionIndex}></div>
            <div className={classes.questionDetails}>               
              <FormControl component="fieldset" disabled={props.fieldDisabled || shouldDisableRadioButtons()}> 
                <RadioGroup
                  row
                  id={subQuestion.subQuestionId}
                  onChange={handleRadioChange(subQuestion.subQuestionId)}
                  defaultValue={props.answer && props.answer[0] ? (props.answer[0].booleanAnswer ? "yes" : "no") : ""}
                >
                  <FormControlLabel value="yes" control={<Radio color="primary" />} label="Approve" />
                  <FormControlLabel value="no" control={<Radio color="primary" />} label="Reject" />
                </RadioGroup>
              </FormControl>
            </div>
          </div>
        </Grid>
      )}

      {/* Render Description Input for Boolean Input Yes */}
      {question.questionId === subQuestion.questionId &&
        subQuestion.answerTypeDescription &&
        subQuestion.answerType &&
        boolAnswer && (
          <Grid item xs={12}>
            <div className={classes.questionParent}>
              <div className={classes.questionIndex}></div>
              <div className={classes.questionDetails}>
                <TextField
                  name={subQuestion.subQuestionId + ""}
                  defaultValue={props.answer && props.answer[0] ? props.answer[0].descriptionAnswer : ""}
                  size="small"
                  onChange={handleFormChange(subQuestion.subQuestionId)}
                  label={subQuestion.description}
                  autoComplete="nope"
                  error={props.isError}
                  helperText={props.isError ? "Required" : null}
                  variant="outlined"
                  disabled={props.answer[0].descriptionAnswer !== "" || props.fieldDisabled}
                  fullWidth
                />
              </div>
            </div>
          </Grid>
        )}

      {/* Render Description Input Only */}
      {question.questionId === subQuestion.questionId &&
        subQuestion.answerTypeDescription &&
        !subQuestion.answerType && (
          <Grid item xs={12} style={{ marginTop: 20 }}>
            <div className={classes.questionParent}>
              <div className={classes.questionIndex}></div>

              <div className={classes.questionDetails}>
                <TextField
                  name={subQuestion.subQuestionId + ""}
                  defaultValue={props.answer && props.answer[0] ? props.answer[0].descriptionAnswer : ""}
                  size="small"
                  onChange={handleFormChange(subQuestion.subQuestionId)}
                  label={subQuestion.description}
                  autoComplete="nope"
                  error={props.isError}
                  helperText={props.isError ? "Required" : null}
                  variant="outlined"
                  disabled={category.categoryId === 3 || (props.fieldDisabled ? true : false)}
                  minRows={category.categoryId === 7 ? 5 : 3}
                  multiline={category.categoryId === 7 || category.categoryId === 8}
                  fullWidth
                />
              </div>
            </div>
          </Grid>
        )}

      {/* Render File uploads */}
      {question.questionId === subQuestion.questionId && subQuestion.answerTypeFileUpload && (
      <Grid item xs={12}>
        <div className={classes.questionParent}>
          <div className={classes.questionIndex}></div>
          <div className={classes.questionDetails}>
            {subQuestion.description !== "" && (
              <Typography
                style={
                  subQuestion.subQuestionId === FINANCE_CHOOSE_FILE_SUBQUESTION_ID
                    ? { marginTop: 7, marginBottom: 16 }
                    : { marginBottom: 16 }
                }
              >
                {subQuestion.description}
              </Typography>
            )}

            {subQuestion.subQuestionId === FINANCE_CHOOSE_FILE_SUBQUESTION_ID ? (
              <>
                <div
                  className={`${classes.dragDropZone} ${
                    isDragging ? classes.dragDropZoneActive : ""
                  } ${isFileLimitReached ? classes.dragDropZoneDisabled : ""}`}
                  onDragEnter={handleDragEnter}
                  onDragOver={handleDragOver}
                  onDragLeave={handleDragLeave}
                  onDrop={(e) => handleDrop(e, subQuestion.subQuestionId)}
                  onClick={() => handleZoneClick(subQuestion.subQuestionId)}
                >
                  <CloudUploadIcon className={classes.uploadIcon} />
                  <div className={classes.uploadText}>
                    <span className={classes.uploadTextLink}>
                      {isFileLimitReached ? "Maximum files reached" : "Click to upload"}
                    </span>
                    {!isFileLimitReached && " or drag and drop"}
                  </div>
                  <div className={classes.uploadSubText}>
                    {isFileLimitReached
                      ? `(${uploadedFiles.length + uploadedNewFile.length}/${FILE_UPLOAD_LIMIT} files uploaded)`
                      : "PDF, JPEG, PNG, JPG (max 5 files)"
                    }
                  </div>
                </div>
                <input
                  type="file"
                  onChange={handleFileUpload(subQuestion.subQuestionId)}
                  id={`file-upload-${subQuestion.subQuestionId}`}
                  multiple
                  className={classes.fileInput}
                  disabled={isFileLimitReached}
                />

                {uploadedNewFile.length > 0 && (
                  <div style={{ marginBottom: 10 }}>
                    <div className={classes.attachedFilesSection}>
                      <span className={classes.attachedFilesLabel}>Attached files:</span>
                      <div>
                        {uploadedNewFile.map((file, key) => (
                          <div key={key} className={classes.fileBlock}>
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                flex: 1,
                              }}
                              onClick={() => handleChipOnClick(file.fileName)}
                            >
                              <InsertDriveFileIcon className={classes.fileIcon} />
                              <span className={classes.fileName}>
                                {formatDisplayFileName(file.fileName)}
                              </span>
                            </div>
                            <CloseIcon
                              className={classes.fileCloseIcon}
                              onClick={(e) => {
                                e.stopPropagation();
                                handleFileRemove(file);
                              }}
                            />
                          </div>
                        ))}
                        {fileUploadProgress && (
                                            <LinearProgress className={classes.linearProgress} color="secondary" />
                                          )}
                      </div>
                    </div>

                    <div className={classes.actionButtons}>
                      <Button
                        className={classes.saveButton}
                        variant="contained"
                        color="secondary"
                        size="small"
                        onClick={saveNewFiles}
                      >
                        Save
                      </Button>
                      <Button
                        className={classes.cancelBtn}
                        variant="outlined"
                        size="small"
                        onClick={cancelNewFiles}
                      >
                        Cancel
                      </Button>
                    </div>
                  </div>
                )}

                {uploadedFiles.length > 0 && (
                  <div className={classes.attachedFilesSection}>
                    {uploadedFiles.map((file, key) => (
                      <div 
                        key={key} 
                        className={classes.fileBlock}
                        onClick={() => handleChipOnClick(file.fileName)}
                      >
                        <InsertDriveFileIcon className={classes.fileIcon} />
                        <span className={classes.fileName}>
                          {formatDisplayFileName(file.fileName)}
                        </span>
                      </div>
                    ))}
                  </div>
                )}
              </>
            ) : (
              <div>
                {uploadedFiles.length > 0 && uploadedFiles.map((file, key) => (
                  <div 
                    key={key} 
                    className={classes.fileBlock}
                    onClick={() => handleChipOnClick(file.fileName)}
                    style={{ marginBottom: 8 }}
                  >
                    <InsertDriveFileIcon className={classes.fileIcon} />
                    <span className={classes.fileName}>
                      {formatDisplayFileName(file.fileName)}
                    </span>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </Grid>
    )}
    </React.Fragment>
  );
};

export default SubQuestionDetails;
